import { ModalWidth, ROW_GUTTER, formatDisplayFullDate } from '@/config';
import { Button, Card, Checkbox, Col, Form, InputNumber, Modal, Row, Select, Tabs } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import ZTable, { IColumnType } from '@/components/ZTable';
import { formatCurrency } from '@/helpers/format';
import { getFullName } from '@/helpers/string';
import TabPane from 'antd/lib/tabs/TabPane';
import TextArea from 'antd/lib/input/TextArea';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';

import { RegisterBookingPaymentModel, RegisterCashierInfo, RegisterDetailModel, RegisterReconciliationModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
const columns: IColumnType<RegisterBookingPaymentModel>[] = [
  {
    title: 'ticketBooking_BookingRefNo',
    dataIndex: 'bookingRefNo',
    ellipsis: true,
  },
  {
    title: 'ticketBooking_TicketRefNo',
    dataIndex: 'receiptRefNo',
    ellipsis: true,
  },
  {
    title: 'registerReconciliation_PaymentDate',
    dataIndex: 'paymentDate',
    ellipsis: true,
    render: v => formatDisplayFullDate(v),
    width: 170,
    sorter: true,
  },

  {
    title: 'registerReconciliation_NoOfPassengers',
    dataIndex: 'noOfPassengers',
    width: 150,
  },
  {
    title: 'base.Amount',
    dataIndex: 'amount',
    ellipsis: true,
    width: 170,
    render: v => <b>{formatCurrency(v)}</b>,
    sorter: true,
  },
  {
    title: 'registerReconciliation_CancelledAmount',
    dataIndex: 'cancelledAmount',
    ellipsis: true,
    render: v => <b>{formatCurrency(v)}</b>,
    sorter: true,
  },
  {
    title: 'registerReconciliation_OutStandingAmount',
    dataIndex: 'outStandingAmount',
    ellipsis: true,
    render: v => <b>{formatCurrency(v)}</b>,
    sorter: true,
  },
];

type Props = {
  registerId: string;
  onClose: () => void;
  registerInfo: RegisterDetailModel;
};
export default function RegisterReconciliationModal({ registerId, onClose, registerInfo }: Props) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setIsLoading] = React.useState(false);
  const [isCrossVerified, setIsCrossVerified] = React.useState(true);
  const [reconciliationDetail, setReconciliationDetail] = React.useState<RegisterReconciliationModel>();
  const [cashier, setCashier] = React.useState<RegisterCashierInfo | undefined>();

  const cashierOptions = React.useMemo(() => {
    return registerInfo.cashiers?.map(c => ({ value: c.id, label: getFullName(c.fistName, c.lastName) }));
  }, [registerInfo]);

  const getData = () => {
    apiSdk.RegisterReconciliationService.getReconciliationDetails({
      cashierId: cashier?.id || '',
      registerId: registerId,
    }).then(res => {
      setReconciliationDetail(res);
    });
  };
  React.useEffect(() => {
    getData();
  }, [cashier]);

  React.useEffect(() => {
    form.resetFields();
  }, [cashier]);

  const logReconciliation = () => {
    form.validateFields().then(res => {
      setIsLoading(true);
      apiSdk.RegisterReconciliationService.logReconciliationRecord({
        body: {
          amountDifference: res.amountDifference,
          cashierId: reconciliationDetail?.cashierId || '',
          comments: res.comments,
          isCrossVerified: isCrossVerified,
          registerId: registerId,
        },
      })
        .then(res => {
          if (res.success) {
            getData();
            setIsCrossVerified(true);
            form.resetFields();
          } else {
            defaultApiErrorHandle(res);
          }
        })
        .catch(defaultApiErrorHandle)
        .finally(() => {
          setIsLoading(false);
        });
    });
  };
  return (
    <Modal
      style={{ top: 10 }}
      open={true}
      maskClosable={false}
      width={ModalWidth.extraLarge}
      className="nta-custom-form"
      title={t('registerReconciliation_Title')}
      forceRender={true}
      okText={t('Close')}
      okButtonProps={{ className: 'save-btn' }}
      cancelButtonProps={{ style: { display: 'none' } }}
      onOk={onClose}
      onCancel={onClose}
    >
      <Row gutter={ROW_GUTTER}>
        <Col md={12}>
          <div>
            {t('registerReconciliation_CashierName')}:<b style={{ marginLeft: 10 }}>{reconciliationDetail?.cashierName}</b>
          </div>
          <div>
            {t('registerReconciliation_LastReconciliation')}:
            <b style={{ marginLeft: 10 }}>{formatDisplayFullDate(reconciliationDetail?.lastReconciliationDate)}</b>
          </div>
        </Col>
        <Col md={12}>
          <div>{t('registerReconciliation_SelectCashier')}</div>
          <Select
            onChange={v => setCashier(registerInfo.cashiers?.find(x => x.id === v))}
            value={reconciliationDetail?.cashierId}
            options={cashierOptions}
            className="w-100"
          />
        </Col>
        <Col md={24}>
          <Tabs defaultActiveKey="payment">
            <TabPane tabKey="payment" tab="Reconciliation Bookings Payment">
              <ZTable<RegisterBookingPaymentModel>
                key={cashier?.id}
                columns={columns}
                primaryKey="id"
                url="/registers/reconciliations/get-reconciliation-payment-records"
                bordered
                defaultFilter={{ registerId: registerId, cashierId: cashier?.id }}
                order_by="createdOn"
                order_by_asc={true}
                allowEdit={false}
                allowDelete={false}
                searchBtnAlignLeft
              />
            </TabPane>
          </Tabs>
        </Col>
      </Row>

      <Row gutter={ROW_GUTTER} style={{ marginTop: 20 }}>
        <Col md={12}>
          <Card className="mtopo-card" size="small">
            <div>
              {t('registerReconciliation_BookingTotalAmount')}:
              <b style={{ marginLeft: 10 }}>{formatCurrency(reconciliationDetail?.bookingTotalAmount)}</b>
            </div>
            <div>
              {t('registerReconciliation_ShipmentTotalAmount')}:
              <b style={{ marginLeft: 10 }}>{formatCurrency(reconciliationDetail?.shipmentTotalAmount)}</b>
            </div>
            <div>
              {t('registerReconciliation_ParkingTotalAmount')}:
              <b style={{ marginLeft: 10 }}>{formatCurrency(reconciliationDetail?.parkingTotalAmount)}</b>
            </div>
            <div>
              {t('registerReconciliation_ExpenseTotalAmount')}:
              <b style={{ marginLeft: 10 }}>{formatCurrency(reconciliationDetail?.expenseTotalAmount)}</b>
            </div>
            {/* P4 : avoirs émis par les modifications de billets, hors espèces. */}
            <div title={t('registerReconciliation_CreditNotesHelp')} data-testid="reconciliation-credit-notes">
              {t('registerReconciliation_CreditNotesIssued')}:
              <b style={{ marginLeft: 10 }}>
                {t('registerReconciliation_CreditNotesIssuedValue', {
                  count: reconciliationDetail?.creditNotesIssuedCount ?? 0,
                  amount: formatCurrency(reconciliationDetail?.creditNotesIssuedAmount ?? 0),
                })}
              </b>
            </div>
            {/* P43 : avoirs utilisés aux ventes et parts rendues aux avoirs, hors espèces (non comptés dans le montant attendu). */}
            <div data-testid="reconciliation-credit-notes-used">
              {t('registerReconciliation_CreditNotesUsed')}:
              <b style={{ marginLeft: 10 }}>
                {t('registerReconciliation_CreditNotesUsedValue', {
                  count: reconciliationDetail?.creditNotesUsedCount ?? 0,
                  amount: formatCurrency(reconciliationDetail?.creditNotesUsedAmount ?? 0),
                  returned: formatCurrency(reconciliationDetail?.creditNotesReturnedAmount ?? 0),
                })}
              </b>
            </div>
          </Card>
        </Col>
        <Col md={12}>
          <Card className="mtopo-card" size="small" loading={loading}>
            <Form form={form} size="middle" layout="vertical">
              <Form.Item>
                <Checkbox
                  checked={isCrossVerified}
                  onChange={e => {
                    setIsCrossVerified(x => !x);
                  }}
                >
                  {t('registerReconciliation_IsAmountVerified')}
                </Checkbox>
              </Form.Item>
              <Form.Item name="comments">
                <TextArea placeholder={t('base.Comment')} />
              </Form.Item>
              <Form.Item label={t('')} name="amountDifference">
                <InputNumber addonAfter="FCFA" disabled={isCrossVerified} />
              </Form.Item>
              <Button
                disabled={!reconciliationDetail}
                htmlType="button"
                type="primary"
                onClick={() => {
                  logReconciliation();
                }}
              >
                {t('registerReconciliation_LogReconciliation')}
              </Button>
            </Form>
          </Card>
        </Col>
      </Row>
    </Modal>
  );
}
